import {
  createIdentitySession,
  publishIdentity,
  createRetentionProvider,
  toAyDimension,
  type ConsentState,
  type IdentitySession,
} from '@redbrickmedia/duplex.user-identity'
import { OpenFeature } from '@openfeature/web-sdk'
import { useAnalytics } from '../composables/analytics/useAnalytics'
import { preAYScripts } from '../utils/ad-ops/preAYScripts'
import {
  identityConsentState,
  resolveConsent,
  subscribeToConsent,
  type ConsentDecision,
} from '../utils/consent/consentGate'
import debug from '../utils/console'

const logger = debug.withTag('plugins:user-identity')

/** AY custom dimension naming the provider that resolved the HEM. */
const HEM_PROVIDER_DIMENSION = 'custom_13'

/**
 * Run `fn` once Assertive Yield is up.
 *
 * Waiting for consent means this can now be called either side of the AY
 * script load, and `assertiveQueue` only covers the "before" case — it is a
 * plain array the vendor drains on load. Once AY is up the queue may never be
 * read again, so call straight through instead of pushing into it.
 */
const afterAy = (fn: () => void) => {
  if (window.assertive) {
    fn()
    return
  }
  window.assertiveQueue = window.assertiveQueue || []
  window.assertiveQueue.push(fn)
}

/**
 * Writing straight into `window.assertive.analytics.custom` on a consent
 * click can race AY's own refresh-cycle targeting and trip its "Improper use
 * of ad refresh" check. Defer the write to `beforeRefresh` — AY's hook for
 * "before the auctions run", the point it expects targeting changes — so the
 * write lands ahead of the very refresh a consent `allow` triggers. The patch
 * is cleared once applied, so the persistent listener writes exactly once.
 */
let pendingHemPatch: Record<string, unknown> | null = null
let beforeRefreshHandlerRegistered = false

const applyPendingHemPatch = () => {
  if (pendingHemPatch && window.assertive?.analytics?.custom) {
    Object.assign(window.assertive.analytics.custom, pendingHemPatch)
    pendingHemPatch = null
  }
}

const queueHemPatch = (patch: Record<string, unknown>) => {
  pendingHemPatch = patch

  if (!beforeRefreshHandlerRegistered) {
    beforeRefreshHandlerRegistered = true
    if (!window.ayManagerEnv) {
      Reflect.set(window, 'ayManagerEnv', { cmd: [] })
    }
    window.ayManagerEnv?.cmd.push(() => {
      window.ayManagerEnv?.onEvent('beforeRefresh', applyPendingHemPatch)
    })
  }
}

export default defineNuxtPlugin({
  name: 'user-identity',
  dependsOn: ['openfeature', 'user-segment'],
  setup: async () => {
    // Both of these are unconditional and deliberately ahead of the
    // `enableUserIdentity` check below: the ad and Google Consent Mode gates
    // read the same verdict, and neither is scoped to the identity
    // integration. They also run before the first `await` so the clock starts
    // as early as this plugin can start it.
    //
    // `preAYScripts()` is what loads the CMP the verdict waits on. `assertive`
    // awaits the same call later, but the script loader memoizes by src, so it
    // receives this in-flight promise and AY still bootstraps strictly after
    // it. Requesting the CMP here rather than there buys back most of the
    // consent budget.
    void preAYScripts()
    void resolveConsent(useNuxtApp().$filters?.country)

    const { client } = await useOpenFeature()
    if (!client.getBooleanValue('enableUserIdentity', false)) {
      return
    }

    const { targetingKey } = OpenFeature.getContext()
    if (typeof targetingKey !== 'string' || !targetingKey) {
      logger.warn('No targetingKey available; skipping identity capture')
      return
    }

    const { track } = useAnalytics()

    let session: IdentitySession | null = null
    let sessionHasProvider = false
    let identityAllowed = false
    let lastState: ConsentState | null = null

    const createSession = (state: ConsentState): IdentitySession | null => {
      try {
        const withProvider = state === 'allow'
        const created = createIdentitySession({
          userId: targetingKey,
          consentState: state,
          provider: withProvider
            ? createRetentionProvider({
                onEvent: (event) =>
                  event.type === 'script:load'
                    ? track('NtpRetention_ScriptLoaded', {
                        success: event.success,
                      })
                    : track('NtpRetention_MatchResult', {
                        hem_resolved: event.hemResolved,
                        status: event.status,
                      }),
              })
            : undefined,
        })
        sessionHasProvider = withProvider
        return created
      } catch (error) {
        sessionHasProvider = false
        logger.warn('Failed to start identity session', error)
        return null
      }
    }

    const publish = (candidate: IdentitySession) => {
      if (!identityAllowed || session !== candidate) return
      try {
        publishIdentity(candidate, { sendToGAM: false })
      } catch {
        logger.warn('Failed to publish identity to Prebid')
      }
    }

    const syncAyHemProvider = (candidate: IdentitySession) => {
      if (!identityAllowed || session !== candidate) return
      const patch = toAyDimension(candidate.current, {
        dimension: HEM_PROVIDER_DIMENSION,
      })
      afterAy(() => queueHemPatch(patch))
    }

    const wireSession = (candidate: IdentitySession) => {
      afterAy(() => publish(candidate))
      syncAyHemProvider(candidate)
      candidate.onHem(() => {
        publish(candidate)
        syncAyHemProvider(candidate)
      })
    }

    const applyConsent = (decision: ConsentDecision) => {
      const state = identityConsentState(decision)
      if (state === lastState) return

      identityAllowed = state === 'allow'

      if (!session) {
        if (state === 'unknown') {
          lastState = state
          return
        }
        session = createSession(state)
        if (session) {
          lastState = state
          if (state === 'allow') wireSession(session)
        }
        return
      }

      if (state === 'allow' && !sessionHasProvider) {
        session = createSession(state)
        if (session) {
          lastState = state
          wireSession(session)
        }
        return
      }

      try {
        session.revise(state)
      } catch (error) {
        logger.warn('Failed to revise identity consent', error)
        return
      }

      lastState = state
      if (state === 'allow') {
        afterAy(() => {
          if (session) publish(session)
        })
        if (session) syncAyHemProvider(session)
      } else if (state === 'deny') {
        logger.info(
          `Identity declined (evidence: ${decision.evidence.join(',') || 'none'})`
        )
      }
    }

    subscribeToConsent(applyConsent)
  },
})
